import * as Switch from "@radix-ui/react-switch"
import clsx from "clsx"
import { ComponentProps, useId } from "react"
import { twMerge } from "tailwind-merge"
import { tv } from "tailwind-variants"

type Props = ComponentProps<typeof Switch.Root> & {
  value?: string | number
  checked?: boolean
  onChange?: (value: boolean) => void
  disabled?: boolean
  binary?: boolean
}

const variants = tv({
  slots: {
    base: [
      "flex justify-start items-center w-toggleW h-toggleH bg-glass border border-current rounded-full relative transition-colors duration-200 outline-none enabled:cursor-pointer p-1 data-[disabled]:border-grey-light data-[state=checked]:data-[disabled]:bg-grey-light error:border-error-dark",
    ],
    thumb:
      "block w-toggleBall h-toggleBall bg-current rounded-full transition-transform duration-200 translate-x-0 will-change-transform data-[state=checked]:translate-x-[23px] data-[disabled]:bg-grey-light data-[state=checked]:data-[disabled]:bg-glass error:bg-error-dark",
  },
  variants: {
    binary: {
      true: {
        base: "hover:bg-grey-light active:bg-grey-light",
      },
      false: {
        base: "data-[state=checked]:bg-current",
        thumb: "data-[state=checked]:bg-glass",
      },
    },
  },
})

export function Toggle(props: Props) {
  const { value, checked, binary, onChange, disabled, className, ...additionalProps } = props
  const styles = variants({
    binary: !!binary,
  })
  return (
    <Switch.Root
      className={twMerge(styles.base(), className)}
      {...additionalProps}
      disabled={disabled}
      value={value}
      checked={checked}
      onCheckedChange={onChange}
    >
      <Switch.Thumb className={styles.thumb()} />
    </Switch.Root>
  )
}

type BinaryToggleProps<T> = Omit<ComponentProps<typeof Switch.Root>, "value"> & {
  left: {
    label: string
    value: T
  }
  right: {
    label: string
    value: T
  }
  value: T
  onToggle: (value: T) => void
}

export function BinaryToggle<T>({ left, right, value, onToggle, ...props }: BinaryToggleProps<T>) {
  return (
    <label className="stack-x-2 items-center cursor-pointer select-none group">
      <span
        className="type-ui-s-roman-caps opacity-50 _active:opacity-100 transition-opacity"
        data-active={value == left.value ? "" : undefined}
      >
        {left.label}
      </span>
      <Toggle
        {...props}
        checked={value === right.value}
        onChange={(checked) => onToggle(checked ? right.value : left.value)}
        binary
      />
      <span
        className="type-ui-s-roman-caps opacity-50 _active:opacity-100 transition-opacity"
        data-active={value == right.value ? "" : undefined}
      >
        {right.label}
      </span>
    </label>
  )
}
